/**
 * Consumo de datos móviles por encuestador.
 *
 * Para qué sirve: el encuestador usa su propio plan de datos para sincronizar
 * entrevistas desde la calle. Esta página dice cuánto, para poder reembolsarlo
 * sin pedirle capturas de pantalla de su teléfono.
 *
 * Qué NO es: una medida del esfuerzo ni del rendimiento. Un encuestador en una
 * zona con buena cobertura de WiFi gastará menos datos que otro en zona rural
 * haciendo el mismo trabajo; §4.2 define estratos rurales precisamente porque
 * el territorio no es homogéneo.
 */

import { useQuery } from '@tanstack/react-query';

import { formatBytes } from '@capi/shared';
import { api } from '../api/client';
import { StatusChip, Tile } from '../components/charts';

export function DataUsagePage() {
  const usage = useQuery({ queryKey: ['telemetry', 'data-usage'], queryFn: api.dataUsage });

  if (usage.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  const data = usage.data;
  if (!data) {
    return <div className="callout callout--critical">No se pudo cargar el consumo.</div>;
  }

  const totalMedido = data.totals.movil + data.totals.wifi + data.totals.desconocida;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Consumo de datos</h1>
        <p className="page-sub">
          Cuántos datos del plan de cada encuestador ha gastado la aplicación de captura. Sólo
          cuenta el tráfico por red móvil: lo que se sincroniza en WiFi no se le cobra a nadie.
        </p>
      </div>

      <div className="grid grid--3">
        <Tile
          label="Datos móviles"
          value={formatBytes(data.totals.movil)}
          meta="Lo reembolsable"
        />
        <Tile label="WiFi" value={formatBytes(data.totals.wifi)} meta="No se cobra" />
        <Tile
          label="Sin clasificar"
          value={formatBytes(data.totals.desconocida)}
          meta={
            data.totals.desconocida > 0
              ? 'No se está contando como móvil'
              : 'Todo el tráfico quedó clasificado'
          }
        />
      </div>

      {/**
       * Esto es deuda, no una curiosidad: mientras haya ASN sin clasificar, el
       * consumo móvil real es MAYOR que el que muestra esta página.
       */}
      {data.sinClasificar.length > 0 ? (
        <div className="callout callout--warning">
          <span className="callout__title">Hay proveedores sin identificar</span>
          Aparecieron {data.sinClasificar.length} sistema(s) autónomo(s) que no están en la tabla,
          con {formatBytes(data.sinClasificar.reduce((a, s) => a + s.bytes, 0))} de tráfico. Ese
          consumo NO se está contando como móvil, así que la cifra de arriba está subestimada.
          Clasifícalos para corregirla.
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>ASN</th>
                  <th>Tráfico</th>
                  <th>Mediciones</th>
                </tr>
              </thead>
              <tbody>
                {data.sinClasificar.map((s) => (
                  <tr key={s.asn ?? 'nulo'}>
                    <td className="strong">{s.asn ? `AS${s.asn}` : 'sin resolver'}</td>
                    <td>{formatBytes(s.bytes)}</td>
                    <td>{s.muestras}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Por encuestador</h2>
            <p className="panel__note">
              Ordenado por consumo móvil. Un encuestador con casi todo su tráfico en WiFi está
              sincronizando en casa y no en campo, que es un dato operativo distinto del gasto.
            </p>
          </div>
        </div>

        {data.porEncuestador.length === 0 ? (
          <div className="callout callout--info">
            Todavía no hay mediciones. Aparecen en cuanto un encuestador sincroniza desde la
            aplicación de captura.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>Encuestador</th>
                  <th>Datos móviles</th>
                  <th>WiFi</th>
                  <th>Sin clasificar</th>
                  <th>Proporción móvil</th>
                  <th>Última medición</th>
                </tr>
              </thead>
              <tbody>
                {data.porEncuestador.map((u) => {
                  const total = u.movil + u.wifi + u.desconocida;
                  const share = total > 0 ? u.movil / total : 0;
                  return (
                    <tr key={u.userId}>
                      <td>
                        <div className="strong">{u.name}</div>
                        <div className="faint">{u.code}</div>
                      </td>
                      <td className="strong">{formatBytes(u.movil)}</td>
                      <td>{formatBytes(u.wifi)}</td>
                      <td>{u.desconocida > 0 ? formatBytes(u.desconocida) : '—'}</td>
                      <td>
                        {/* El color NO carga el significado solo: va con el
                            porcentaje escrito al lado. */}
                        <StatusChip status={share > 0.8 ? 'warning' : 'neutral'}>
                          {(share * 100).toFixed(0)}%
                        </StatusChip>
                      </td>
                      <td className="faint">
                        {u.lastSeen
                          ? new Date(u.lastSeen).toLocaleString('es-MX', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Cómo se clasifica</h2>
            <p className="panel__note">
              El navegador NO puede decir si el teléfono está en WiFi o en datos móviles:
              `navigator.connection.type` no existe, se retiró por ser huella digital. Lo que se
              usa es el proveedor desde el que llega la petición, y el encuestador puede
              corregirlo desde su aplicación. No se guarda ninguna dirección IP.
            </p>
          </div>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>ASN</th>
                <th>Proveedor</th>
                <th>Tipo</th>
                <th>Razón social</th>
              </tr>
            </thead>
            <tbody>
              {data.isps.map((isp) => (
                <tr key={isp.asn}>
                  <td className="mono">AS{isp.asn}</td>
                  <td className="strong">{isp.name}</td>
                  <td>
                    <StatusChip status={isp.kind === 'movil' ? 'warning' : 'good'}>
                      {isp.kind === 'movil' ? 'Móvil' : 'Fijo'}
                    </StatusChip>
                  </td>
                  <td className="faint">{isp.registeredAs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="panel__note" style={{ marginTop: 12 }}>
          Total medido: {formatBytes(totalMedido)}.
        </p>
      </div>
    </>
  );
}
